// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.26.0, gate E12.5 round 1 (rehearsal R1) — the one id a person is
 * known by on a record.
 *
 * ## Two ids, one identity
 *
 * A `grc_users` document can carry two ids:
 *
 * - `_id`, a ULID. The server's session identity (`session.user_id`) is this
 *   value, and so is every `created_by`, `assembled_by`, `requested_by`,
 *   `assigned_to`, role assignment and audit actor a route writes.
 * - `user_id`, a UUID that `UserService.create` (setup and the Users page)
 *   writes beside it. Nothing compares a record's actor with it.
 *
 * The login response names the session identity `user_id` and sends no `_id`;
 * `GET /auth/me` and `GET /users` return the stored document, with both.
 *
 * Before this accessor, `AuthContext` read `user.user_id || user._id` from
 * `GET /auth/me`, so every account created through setup or Users carried the
 * UUID as its id, and every "is this me?" check on the page — the assembler who
 * may not review, the reviewer's own queue, the recipient who may acknowledge —
 * compared it with a ULID and failed. Seeded dev and e2e users have no
 * `user_id` field, which is why no test saw it.
 *
 * ## The rule
 *
 * `_id` first, `user_id` only when there is no `_id`. That one order is right
 * for all three shapes: the stored document (`_id` is the identity), the login
 * response (`user_id` IS the identity and there is no `_id`), and the UI's own
 * `UserInfo` (`user_id`, already derived through here).
 *
 * ## Only for user documents
 *
 * A row that REFERENCES a user — a department mapping, a group membership, a
 * session, an SoD exception — also has a `user_id`, and there it IS the
 * person's id while `_id` is the row's. Do not pass such a row here: this
 * function would return the row's id (gate E12.5 pass 2, P1-1, the dossier
 * department picker). Read the row's own `user_id`.
 *
 * ## What the guard covers
 *
 * `__tests__/user-record-id-63.26.0.test.tsx` reads every non-test source file
 * and fails on: a reader that takes `user_id` off the signed-in user directly
 * (in any file, however `useAuth()` or `useContext(AuthContext)` is bound); and,
 * in a file that reads user documents (`listUsers(`, `getMe(`, `getUser(`, or
 * the auth user), `x.user_id || x._id`, `value: x.user_id`, a lookup
 * (`.find` / `.some` / `.filter` / `.findIndex` comparing `x.user_id`), a row
 * `key={x.user_id}`, `${x.user_id}` in a template (a link or a label) and
 * `apiClient.…(x.user_id)` (Sprint 63.32.0, #546). It is a line-level pattern
 * check: it does not follow a list across variables or files, and it cannot see
 * a row that references a user being passed here. A row-referencing site is
 * marked `// row-references-user: <RowType>` (or `{/* … *\/}` before a JSX
 * list) within two lines above it, exempting that site only, and is named in the
 * test's own register.
 */

type IdBearing = { _id?: unknown; user_id?: unknown } | null | undefined;

const nonEmpty = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/** The id a record names this person by, or `null` when the value carries none. */
export function userRecordIdOf(value: IdBearing): string | null {
  if (!value) return null;
  return nonEmpty(value._id) ?? nonEmpty(value.user_id);
}
